import { Col, Row } from "antd";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import NoJobPositions from "../../Components/NoJobPositions";
import { openNotification } from "../../Components/Notification/Index";
import { useAuth } from "../../Context/AuthContext/Index";
import LoaderContext from "../../Context/LoaderContext/Index";
import { findAllJobPostDto } from "../../Interface/Index";
import JobApplicationService from "../../Services/job.application.service";
import useExpiry from "../../Hooks/useExpiry";
import PlanExpired from "../../Components/PlanExpired";
import { useJobDescription } from "../../Hooks/useJobDescription";
import { useJobApplication } from "../../Hooks/useJobApplication";
import { useDebounce } from "../../Hooks/useDebounce";
import AgentInitializer from "./components/AgentInitializer";
import JobPositionsFilterMenu from "./components/JobPositionsFilterMenu";
import ListingPageContent from "./components/ListingPageContent";
import useOrganization from "../../Hooks/useOrganization";
import "./index.scss";

const Listing: React.FC = () => {
  const { isUserLoggedIn } = useAuth();
  const { setLoading, setLoadingMessage } = useContext(LoaderContext);
  const navigate = useNavigate();
  const { isBasePlanExpired } = useExpiry();
  const {organization} =useOrganization();

  // Search state
  const [searchQuery, setSearchQuery] = useState<string>("");
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  // Filters state
  const [filters, setFilters] = useState<findAllJobPostDto>({
    page: 1,
    limit: 10,
    position: [],
    searchQuery: null,
  });

  // Data hooks
  const { jobDescriptions, positionsWithOpeningCount, pagination } =
    useJobDescription(filters);
  const {
    appliedJobPostIds,
    applications,
    refetch: fetchApplicationIds,
  } = useJobApplication();

  // Effect for search query
  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      searchQuery: debouncedSearchQuery || null,
      page: 1,
    }));
  }, [debouncedSearchQuery]);

  // Handlers
  const handleViewDetails = useCallback(
    (_id: string) => {
      navigate(`/${_id}/requirements`);
    },
    [navigate]
  );

  const handleApply = useCallback(
    async (jobPostId: string) => {
      if (!isUserLoggedIn) {
        openNotification("error", "Please sign in to apply for a job", "");
        navigate(`/sign-in?jdId=${jobPostId}`);
        return;
      }

      setLoading(true);
      setLoadingMessage("sending application please wait...");
      try {
        const jobPostName = jobDescriptions.find(
          (jd) => jd._id === jobPostId
        )?.title;
        const response = await new JobApplicationService().sendJobApplication(
          jobPostId,
          applications,
          organization?.orgName || "QualifyMe",
          jobPostName || "Unknown Job Title"
        );
        if (response?._id) {
          openNotification("success", "Applied successfully!", "");
          await fetchApplicationIds();
          navigate(
            `/${jobPostId}/requirements?jobApplicationId=${response._id}`
          );
        }
      } catch (error: any) {
        console.error("Error in handleApply:", error);
        openNotification("error", "Error in handleApply", "");
      } finally {
        setLoading(false);
        setLoadingMessage(null);
      }
    },
    [
      isUserLoggedIn,
      navigate,
      setLoading,
      setLoadingMessage,
      fetchApplicationIds,
      applications,
    ]
  );

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  }, []);

  if (isBasePlanExpired) {
    return <PlanExpired />;
  }

  if (!positionsWithOpeningCount?.length) {
    return <NoJobPositions />;
  }

  return (
    <section className="listing-page" style={{ height: "100%" }}>
      <AgentInitializer isUserLoggedIn={isUserLoggedIn} />
      <Row style={{ height: "100%" }}>
        <Col
          xs={{ span: 24 }}
          sm={{ span: 24 }}
          md={{ span: 8 }}
          lg={{ span: 6 }}
          xl={{ span: 4 }}
          style={{ height: "100%" }}
          className="listing_page_dashboard_filter_menu_col"
        >
          <JobPositionsFilterMenu
            filters={filters}
            positionsWithOpeningCount={positionsWithOpeningCount}
            setFilters={setFilters}
          />
        </Col>
        <Col
          xs={{ span: 24 }}
          sm={{ span: 24 }}
          md={{ span: 16 }}
          lg={{ span: 18 }}
          xl={{ span: 20 }}
          style={{ height: "100%" }}
        >
          <ListingPageContent
            handleSearch={handleSearch}
            appliedJobPostIds={appliedJobPostIds}
            jobDescriptions={jobDescriptions}
            filters={filters}
            pagination={pagination}
            handlePageChange={handlePageChange}
            handleViewDetails={handleViewDetails}
            handleApply={handleApply}
            positionsWithOpeningCount={positionsWithOpeningCount}
            setFilters={setFilters}
          />
        </Col>
      </Row>
    </section>
  );
};

export default Listing;
